import { Link } from 'react-router-dom';
import { LegalPage, LegalSection, MailLink } from '../components/LegalPage';
import { useSiteContent } from '../hooks/useSiteContent';
import { useShippingMethods } from '../hooks/useShippingMethods';
import { WEIGHT_BASED_METHOD } from '../lib/shipping';

const euro = (value: number) =>
  value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

// Ultima modifica al testo di questa pagina. La data mostrata è la più
// recente tra questa e l'ultima modifica ai prezzi dalla dashboard (stessa
// regola in scripts/prerender.js).
const TEXT_UPDATED_AT = '2026-10-28';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Rome' });

export default function ShippingPolicy() {
  const { content: contacts } = useSiteContent('site.contacts');
  const { methods, tiers, lastUpdated, loading, error } = useShippingMethods();
  const activeMethods = methods.filter((m) => m.active);
  const freeShippingFrom = activeMethods.find((m) => m.value === WEIGHT_BASED_METHOD)?.freeFrom ?? null;

  return (
    <LegalPage
      title="Spedizioni"
      description="Metodi, costi e tempi di spedizione dei prodotti Apicoltura DePiccoli in Italia."
      lastUpdated={formatDate(lastUpdated && lastUpdated > TEXT_UPDATED_AT ? lastUpdated : TEXT_UPDATED_AT)}
    >
      <LegalSection title="Dove spediamo">
        <p>Spediamo in tutta Italia. Al momento non effettuiamo spedizioni all'estero.</p>
      </LegalSection>

      <LegalSection title="Metodi e costi">
        {loading && <p>Caricamento dei costi di spedizione…</p>}
        {error && <p>{error}: il costo esatto è comunque sempre mostrato nel carrello prima del pagamento.</p>}
        {activeMethods.length > 0 && (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {activeMethods.map((method) => (
              <li key={method.value} className="p-4">
                <div className="flex items-baseline justify-between gap-4">
                  <span className="font-medium text-foreground">{method.label}</span>
                  <span className="text-right whitespace-nowrap">
                    {method.value === WEIGHT_BASED_METHOD
                      ? tiers.length > 0 && `da ${euro(Math.min(...tiers.map((t) => t.price)))}`
                      : method.price === 0 ? 'Gratis' : euro(method.price)}
                    {method.freeFrom !== null && (method.price > 0 || method.value === WEIGHT_BASED_METHOD) && (
                      <span className="block text-xs">gratis da {euro(method.freeFrom)}</span>
                    )}
                  </span>
                </div>
                <p className="mt-1 text-sm">{method.description}</p>
                {method.value === WEIGHT_BASED_METHOD && tiers.length > 0 && (
                  <table className="mt-3 w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground">
                        <th className="py-1 font-medium">Peso del pacco imballato</th>
                        <th className="py-1 text-right font-medium">Costo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tiers.map((t, i) => (
                        <tr key={t.max_grams} className="border-t border-border">
                          <td className="py-1.5">
                            {i === 0 ? 'fino a ' : `${(tiers[i - 1].max_grams / 1000).toLocaleString('it-IT')}–`}
                            {(t.max_grams / 1000).toLocaleString('it-IT')} kg
                          </td>
                          <td className="py-1.5 text-right">{euro(t.price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </li>
            ))}
          </ul>
        )}
        <p>
          {freeShippingFrom !== null && (
            <>
              La spedizione standard è gratuita per ordini da {euro(freeShippingFrom)} in su
              (importo dei prodotti, IVA inclusa).{' '}
            </>
          )}
          Spediamo con Poste Italiane (Poste Delivery Web) e il costo dipende dal peso del pacco, vasetti e
          imballo compresi: il costo esatto è sempre mostrato nel carrello prima del pagamento. Al momento non
          offriamo la spedizione espressa.
        </p>
      </LegalSection>

      <LegalSection title="Tempi di preparazione e consegna">
        <p>
          Gli ordini vengono preparati nei giorni lavorativi successivi alla conferma del
          pagamento. I tempi indicati sopra sono quelli del corriere e possono allungarsi in
          periodi di picco o per cause non dipendenti da noi. In ogni caso la consegna avviene
          entro 30 giorni dall'ordine.
        </p>
        <p>
          Quando l'ordine viene spedito ricevi un'email con il numero di tracciamento. Puoi
          seguire lo stato dell'ordine anche dalla pagina{' '}
          <Link to="/orders" className="text-primary hover:underline">
            Traccia ordine
          </Link>
          .
        </p>
      </LegalSection>

      <LegalSection title="Pacco danneggiato o mancata consegna">
        <p>
          Se il pacco arriva danneggiato, se possibile accettalo con riserva indicandolo al
          corriere. In ogni caso scrivici entro 7 giorni dalla consegna a{' '}
          <MailLink email={contacts.email} /> con il numero d'ordine e una foto: provvederemo alla
          sostituzione o al rimborso senza spese. Fino alla consegna il rischio di perdita o
          danneggiamento dei prodotti è a nostro carico. Per i resi vedi{' '}
          <Link to="/resi" className="text-primary hover:underline">
            Diritto di recesso e resi
          </Link>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
}
